import {
  GoogleMap,
  InfoWindow,
  LoadScript,
  Marker
} from '@react-google-maps/api';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { styled } from 'linaria/react';
import t from '@jetshop/intl';
import { LocationStateContext } from './StoreLocator';
import { useMapState } from './useMapState';
import { useShopConfig } from '@jetshop/core/hooks/useShopConfig';
import throwErrorInDev from '@jetshop/core/helpers/throwErrorInDev';
import { ReactComponent as MapMarker } from '../../svg/MapPinFull.svg';
import MapPin from '../../svg/MapPinFull.svg';

import { theme } from '../Theme';

export const mapStyle = [
  {
    featureType: 'administrative',
    elementType: 'labels.text.fill',
    stylers: [
      {
        color: '#444444'
      }
    ]
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#98C5D3'
      },
      {
        visibility: 'on'
      }
    ]
  }
];

const Wrapper = styled('div')`
  width: 100%;
  height: 80vh;
  position: relative;
  ${theme.below.lg} {
    height: auto;
  }

  & > div:first-of-type {
    & > div {
      position: absolute;
      right: 12vw;
      z-index: 1;
      background: #fff;
      padding: 3rem 4rem;
      top: 50%;
      transform: translateY(-50%);
      max-width: 400px;
      width: 100%;
      max-height: 90%;
      overflow: scroll;
      ${theme.below.lg} {
        position: relative;
        right: auto;
        top: 0;
        transform: none;
      }

      h1 {
        border-bottom: 3px solid #d2d2d2;
        padding-bottom: 5px;
        margin-bottom: 10px;
        line-height: 35px;
      }
      span {
        font-size: 14px;
      }
      div {
        margin-bottom: 15px;
      }
      a {
        margin-bottom: 10px;
        display: block;
        color: #000;
        font-weight: bold;
      }
      .directions-link {
        display: flex;
        padding-bottom: 10px;
        svg {
          max-width: 1rem;
          margin-right: 10px;
        }
      }
    }
  }

  .map-container {
    min-height: 100%;

    ${theme.below.lg} {
      height: 50vh;
    }
  }
`;

const SelectedStoreWrapper = styled('div')`
  h1 {
    border-bottom: 3px solid #d2d2d2;
    padding-bottom: 5px;
    margin-bottom: 10px;
  }
  span {
    font-size: 14px;
  }
  div {
    margin-bottom: 10px;
  }
  .directions-link {
    display: flex;
    padding-bottom: 10px;
    svg {
      max-width: 1rem;
      margin-right: 10px;
    }
  }
  h2 {
    margin-bottom: 1rem;
  }
  h3 {
    font-weight: 400;
    cursor: pointer;
    span {
      font-size: 0.65em;
    }
  }
  table {
    th,
    td {
      padding-right: 0.5rem;
    }
    th {
      text-align: left;
    }
  }
`;

const LoadingElement = () => <div style={{ height: '100%' }}></div>;

const StoreMap = ({
  stores,
  userLocation,
  closestStore,
  selectedStore,
  setSelectedStore,
  selectedRegion,
  filteredStores
}) => {
  const ref = useRef();
  const locationState = useContext(LocationStateContext);
  const { isOpen, infoId, showInfo } = useMapState();
  const [mapsLoaded, setMapsLoaded] = useState(false);

  const { googleMapsApiKey } = useShopConfig();

  throwErrorInDev(
    typeof googleMapsApiKey === 'undefined',
    'Make sure googleMapsApiKey is defined in your shop.config.js. See https://developers.google.com/maps/documentation/javascript/get-api-key'
  );

  useEffect(() => {
    if (stores && mapsLoaded) {
      // Stores are loaded, fit bounds
      const bounds = new window.google.maps.LatLngBounds();

      if (filteredStores) {
        filteredStores.forEach(store => {
          if (store.other !== null) {
            var other = store.other
              .replace(/<[^>]*>/g, '')
              .replace(/\r?\n|\r/g, '-')
              .split('-');

            var lat = parseFloat(other[0]);
            var lng = parseFloat(other[1]);

            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        });
      } else {
        stores.forEach(store => {
          if (store.other !== null) {
            var other = store.other
              .replace(/<[^>]*>/g, '')
              .replace(/\r?\n|\r/g, '-')
              .split('-');

            var lat = parseFloat(other[0]);
            var lng = parseFloat(other[1]);

            bounds.extend(new window.google.maps.LatLng(lat, lng));
          }
        });
      }

      // Don't zoom in too far on only one marker
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01
        );
        const extendPoint2 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01
        );
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }

      ref.current.fitBounds(bounds);
    }
  }, [mapsLoaded, stores, selectedRegion, filteredStores]);

  useEffect(() => {
    if (closestStore && mapsLoaded) {
      // User has shared its location and the closest store has been calculated
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend(
        new window.google.maps.LatLng(
          userLocation.latitude,
          userLocation.longitude
        )
      );
      closestStore.forEach(cs => {
        var other = cs.other
          ?.replace(/<[^>]*>/g, '')
          ?.replace(/\r?\n|\r/g, '-')
          ?.split('-');

        var lat = parseFloat(other[0]);
        var lng = parseFloat(other[1]);
        bounds.extend(new window.google.maps.LatLng(lat, lng));
      });

      ref.current.fitBounds(bounds);
    }
  }, [userLocation, closestStore, mapsLoaded]);

  useEffect(() => {
    if (selectedStore && mapsLoaded) {
      // User has shared its location and the closest store has been calculated
      const bounds = new window.google.maps.LatLngBounds();

      if (selectedStore.other !== null) {
        var other = selectedStore.other
          ?.replace(/<[^>]*>/g, '')
          ?.replace(/\r?\n|\r/g, '-')
          ?.split('-');

        var lat = parseFloat(other[0]);
        var lng = parseFloat(other[1]);
      }

      bounds.extend(new window.google.maps.LatLng(lat, lng));
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() + 0.01,
          bounds.getNorthEast().lng() + 0.01
        );
        const extendPoint2 = new window.google.maps.LatLng(
          bounds.getNorthEast().lat() - 0.01,
          bounds.getNorthEast().lng() - 0.01
        );
        bounds.extend(extendPoint1);
        bounds.extend(extendPoint2);
      }
      ref.current.fitBounds(bounds);
    } else if (!selectedStore && mapsLoaded) {
      // const bounds = new window.google.maps.LatLngBounds();
      // bounds.extend(new window.google.maps.LatLng(59.247948, 14.755806));
      // if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      //   const extendPoint1 = new window.google.maps.LatLng(
      //     bounds.getNorthEast().lat() + 5,
      //     bounds.getNorthEast().lng() + 5
      //   );
      //   const extendPoint2 = new window.google.maps.LatLng(
      //     bounds.getNorthEast().lat() - 5,
      //     bounds.getNorthEast().lng() - 5
      //   );
      //   bounds.extend(extendPoint1);
      //   bounds.extend(extendPoint2);
      // }
      // ref.current.fitBounds(bounds);
    }
  }, [selectedStore, mapsLoaded]);

  useEffect(() => {
    const storeLocatorHeader = document.getElementById('store-locator-header');
    const storeDetailHeader = document.getElementById('store-detail-header');
    if (storeLocatorHeader && storeDetailHeader) {
      const h2 = document.createElement('h2');
      h2.id = 'store-locator-header';
      h2.innerHTML = storeLocatorHeader.innerHTML;
      h2.className = storeLocatorHeader.className;
      
      storeLocatorHeader.parentNode.replaceChild(h2, storeLocatorHeader);
    }
  }, [selectedStore?.name]);
  const storeName = selectedStore?.region === 'Sverige' ? 'Hööks ' + selectedStore?.name : selectedStore?.name;

  function stripHtmlTags(str) {
    return str.replace(/<[^>]*>/g, '');
  }

  return (
    <Wrapper id="mapWrap">
      {closestStore && !selectedStore && userLocation && (
        <SelectedStoreWrapper>
          <div className="closestStores">
            <h2>{t('Your closest stores')}</h2>
            {closestStore.map(store => (
              <h3
                onClick={() => {
                  setSelectedStore(store);
                }}
              >
                {store.name}{' '}
                <span>{parseFloat(store?.distance).toFixed(1)} km</span>
              </h3>
            ))}
          </div>
        </SelectedStoreWrapper>
      )}

      {selectedStore && (
        <SelectedStoreWrapper>
          <div>
            <h1 id="store-detail-header">{storeName}</h1>
            <a
              className="directions-link"
              target="_blank"
              rel="noreferrer"
              href={
                'https://www.google.com/maps/search/?api=1&query='+stripHtmlTags(selectedStore.address1)
              }
            >
              <MapMarker />
              {t('Directions')}
            </a>
            <div className="address">
              <span
                dangerouslySetInnerHTML={{
                  __html: selectedStore?.address1
                }}
              />
            </div>
            <div className="contact">
              <span
                dangerouslySetInnerHTML={{
                  __html: selectedStore?.contact
                }}
              />
            </div>
            <div className="opening-hours">
              <span
                dangerouslySetInnerHTML={{
                  __html: selectedStore?.openHours
                }}
              />
            </div>
            <div className="odd-opening-hours">
              <span
                dangerouslySetInnerHTML={{
                  __html: selectedStore?.description
                }}
              />
            </div>
          </div>
        </SelectedStoreWrapper>
      )}
      <>
        <LoadScript
          loadingElement={<LoadingElement />}
          preventGoogleFontsLoading={false}
          googleMapsApiKey={googleMapsApiKey}
        >
          <GoogleMap
            maxZoom={10}
            mapContainerClassName="map-container"
            defaultCenter={{ lat: 59.247948, lng: 14.755806 }}
            onLoad={map => {
              ref.current = map;
              setMapsLoaded(true);
            }}
            options={{ styles: mapStyle }}
          >
            <>
              {mapsLoaded &&
                stores.map((store, index) => {
                  const isActive = store.id === locationState.activeLocation;

                  if (store.other !== null) {
                    var other = store.other
                      ?.replace(/<[^>]*>/g, '')
                      ?.replace(/\r?\n|\r/g, '-')
                      ?.split('-');

                    var lat = parseFloat(other[0]);
                    var lng = parseFloat(other[1]);
                  } else {
                    return null;
                  }

                  return (
                    <React.Fragment key={store.id}>
                      <Marker
                        clickable={true}
                        cursor={'pointer'}
                        icon={{
                          url: MapPin,
                          fillColor: isActive ? '#ef7622' : '#235838',
                          fillOpacity: 1,
                          strokeColor: 'transparent',
                          anchor: new window.google.maps.Point(12, 32),
                          scaledSize: new window.google.maps.Size(25, 32)
                        }}
                        zIndex={index}
                        position={{
                          lat: lat,
                          lng: lng
                        }}
                        onMouseOver={() => {
                          locationState.setHighlightedLocation(store.id);
                        }}
                        onMouseOut={() =>
                          locationState.setHighlightedLocation(null)
                        }
                        onClick={() => {
                          setSelectedStore(store);
                        }}
                      />
                      {isOpen && infoId === store.id && (
                        <InfoWindow
                          key={`infowindow-${store.id}`}
                          onCloseClick={showInfo}
                          position={{
                            lat: lat,
                            lng: lng
                          }}
                          options={{
                            pixelOffset: new window.google.maps.Size(50, 0)
                          }}
                        >
                          <div
                            style={{
                              background: 'white',
                              padding: '1em',
                              minWidth: '200px',
                              fontSize: '14px',
                              lineHeight: 1.5
                            }}
                          >
                            <p>
                              <strong>{store.name}</strong>
                            </p>
                            <div
                              dangerouslySetInnerHTML={{
                                __html: store.address1
                              }}
                            />
                          </div>
                        </InfoWindow>
                      )}
                    </React.Fragment>
                  );
                })}

              {userLocation.latitude && userLocation.longitude && (
                <Marker
                  position={{
                    lat: userLocation.latitude,
                    lng: userLocation.longitude
                  }}
                  icon={{
                    path:
                      'M19.7,4.2c-2.1-2.1-4.9-3.3-7.9-3.3c-3,0-5.8,1.2-7.9,3.3c-2.1,2.1-3.3,4.9-3.3,7.9c0,8.7,11.2,16.1,11.2,16.1 S23,20.7,23,12.1C23,9.1,21.8,6.3,19.7,4.2z M11.9,4.2c1.6,0,2.8,1.3,2.8,2.8s-1.3,2.8-2.8,2.8S9,8.6,9,7S10.3,4.2,11.9,4.2z  M5.3,17.2c0-3.6,2.9-6.6,6.6-6.6s6.6,2.9,6.6,6.6H5.3z',
                    fillColor: '#ef7622',
                    fillOpacity: 1,
                    strokeColor: 'transparent',
                    anchor: new window.google.maps.Point(12, 32),
                    scaledSize: new window.google.maps.Size(25, 32)
                  }}
                ></Marker>
              )}
            </>
          </GoogleMap>
        </LoadScript>
      </>
    </Wrapper>
  );
};

export default StoreMap;
